import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ApiDatabase from "server";
import UseContext from "hooks/useContext";
import { dateDiff } from "helper/helper";
import { formatFullName } from "helper/personName";
import EmptyState from "components/emptyState";
import Pagination from "components/pagination/pagination";
import StyledInput from "components/styledInput/StyledInput";
import ProfileChoice from "components/profileChoice/ProfileChoice";
import GroupMissionsBadge from "components/profileChoice/GroupMissionsBadge";
import { PositiveThumb, SearchIcon } from "assets/icons";
import useListFillHeight from "pages/createMission/utils/useListFillHeight";
import { INVITED_PROFILE_TYPE, isProfileInvited } from "pages/createMission/utils/invitedProfiles";
import ProfileListSkeleton from "./ProfileListSkeleton";

const PAGE_SIZE = 20;

const hasGroupMissions = (tempWorker) => Number(tempWorker.groupMissions?.total) > 0;

const getTempWorkerName = (tempWorker) =>
    tempWorker.firstname || tempWorker.lastname
        ? formatFullName(tempWorker.firstname, tempWorker.lastname)
        : tempWorker.name;

// "Préparateur de commandes il y a 12 jours chez Antilles Distribution": the last mission
// ended in the past, or "termine dans 2 jours" when it is still running.
const formatLastMission = (tempWorker, companyId, label) => {
    if (!tempWorker.date_end) return "";
    const diff = dateDiff(tempWorker.date_end);
    const isPast = diff.day >= 0;
    const elapsed = isPast ? diff : Object.fromEntries(Object.entries(diff).map(([key, value]) => [key, Math.abs(value)]));
    const plural = (value, one, many) => (Number(value) === 1 ? one : many);
    let duration = "";
    if (elapsed.day > 0) {
        duration = `${elapsed.day} ${plural(elapsed.day, label("day", "jour"), label("days", "jours"))}`;
    } else if (elapsed.hour > 0) {
        duration = `${elapsed.hour} ${plural(elapsed.hour, label("hour", "heure"), label("hours", "heures"))}`;
    } else if (elapsed.min > 0) {
        duration = `${elapsed.min} ${label("minutes", "minutes")}`;
    } else if (elapsed.sec > 0) {
        duration = `${elapsed.sec} ${label("seconds", "secondes")}`;
    }
    if (!duration) return "";
    return [
        tempWorker.title,
        (isPast ? label("ago", "il y a {duration}") : label("endsIn", "termine dans {duration}")).replace(
            "{duration}",
            duration
        ),
        companyId !== tempWorker.id_company && tempWorker.company ? `${label("at", "chez")} ${tempWorker.company}` : "",
    ]
        .filter(Boolean)
        .join(" ");
};

/** "Profils Jobbiz" tab: the temp workers who already worked for the company, filtered here. */
const JobbizProfilesTab = ({ isActive, invitedProfiles, onToggle }) => {
    const { data, companyData, language } = UseContext();
    const label = (key, defaultValue) => language.getValue(`missionCreation.invitation.${key}`, defaultValue);
    const jobbizLabel = (key, defaultValue) => label(`jobbiz.${key}`, defaultValue);
    const listRef = useRef(null);
    const [tempWorkers, setTempWorkers] = useState(null);
    const [searchResult, setSearchResult] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        if (!companyData?._id) return;
        setIsLoading(true);
        ApiDatabase.getTempWorkersRatingByMission(
            {
                idCompany: companyData._id,
                titleMission: data.qualification,
                dateStart: data.dateStart,
                dateEnd: data.dateEnd,
            },
            (response) => {
                setTempWorkers(response);
                setSearchResult(response);
                setIsLoading(false);
            },
            () => {
                setIsLoading(false);
                toast.error(label("loadError", "Une erreur est survenue, veuillez réessayer plus tard."));
            }
        );
        // Loaded once per visit of the step, as before the tabs.
    }, [companyData]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (!Array.isArray(tempWorkers)) return;
            const needle = search.toLowerCase();
            setCurrentPage(1);
            setSearchResult(tempWorkers.filter((tempWorker) => (tempWorker.name || "").toLowerCase().includes(needle)));
        }, 300);
        return () => clearTimeout(timer);
    }, [search, tempWorkers]);

    const countTotal = Array.isArray(searchResult) ? searchResult.length : 0;
    const totalPages = Math.ceil(countTotal / PAGE_SIZE);
    const countPerPage = currentPage === totalPages ? countTotal - (totalPages - 1) * PAGE_SIZE : PAGE_SIZE;
    // Pages start every PAGE_SIZE: the last one, shorter, must not shift them.
    const pageItems = Array.isArray(searchResult)
        ? searchResult.slice((currentPage - 1) * PAGE_SIZE, (currentPage - 1) * PAGE_SIZE + PAGE_SIZE)
        : [];
    const hasPagination = totalPages > 1;

    const listMaxHeight = useListFillHeight(listRef, isActive, `${hasPagination}-${isLoading}-${pageItems.length}`);

    return (
        <div className="flex flex-col gap-4">
            <StyledInput
                type={"text"}
                placeholder={label("searchPlaceholder", "Nom ou prénom")}
                imgFront={<SearchIcon wh={18} color={"#6B7280"} />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />
            <div
                ref={listRef}
                className={"flex flex-col overflow-y-auto border rounded-lg"}
                style={listMaxHeight ? { maxHeight: listMaxHeight } : undefined}
            >
                {isLoading ? (
                    <ProfileListSkeleton />
                ) : pageItems.length > 0 ? (
                    pageItems.map((tempWorker) => {
                        const name = getTempWorkerName(tempWorker);
                        const detail = formatLastMission(tempWorker, companyData?._id, jobbizLabel);
                        return (
                            <ProfileChoice
                                key={tempWorker._id}
                                name={name}
                                detail={detail}
                                checked={isProfileInvited(invitedProfiles, INVITED_PROFILE_TYPE.JOBBIZ, tempWorker._id)}
                                onToggle={() =>
                                    onToggle({ type: INVITED_PROFILE_TYPE.JOBBIZ, id: tempWorker._id, name, detail })
                                }
                                aside={
                                    hasGroupMissions(tempWorker) || tempWorker.isRated ? (
                                        <span className={"flex items-center gap-2"}>
                                            <GroupMissionsBadge name={name} groupMissions={tempWorker.groupMissions} />
                                            {tempWorker.isRated ? (
                                                <span className={"block rounded-full bg-green-50 p-2"}>
                                                    <PositiveThumb wh={20} color={"#10B981"} />
                                                </span>
                                            ) : null}
                                        </span>
                                    ) : null
                                }
                            />
                        );
                    })
                ) : (
                    <EmptyState type={"mission_invitation"} compact />
                )}
            </div>
            {hasPagination ? (
                <Pagination
                    currentPage={currentPage}
                    countPerPage={countPerPage}
                    countTotal={countTotal}
                    totalPages={totalPages}
                    setCurrentPage={setCurrentPage}
                />
            ) : null}
        </div>
    );
};

export default JobbizProfilesTab;
